// external
import { Box, Button, Card, CardContent, Divider, Typography } from '@mui/material';
import React, { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import GroupsIcon from '@mui/icons-material/Groups';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

export interface COETeamCardProps {
  teamId: string;
  name: string;
  color: string;
  status: string;
  description: string;
  plan: string;
  role: string;
  athleteCount?: number;
  coachCount?: number;
  sessionCount?: number;
  billingRequired?: boolean;
}

const ROLE_COLORS: Record<string, string> = {
  coach: '#2563EB',
  athlete: '#16A34A',
};

const PLAN_BADGES: Record<string, string> = {
  free: 'BASIC',
  unlimited: 'LEGACY',
  small_team: 'SMALL',
  program: 'PROGRAM',
  academic: 'ACADEMIC',
  academic_school: 'ACADEMIC',
};

const COETeamCardComponent: FC<COETeamCardProps> = (props) => {
  const navigate = useNavigate();
  const borderColor = ROLE_COLORS[props.role] ?? '#2563EB';
  const roleLabel = props.role === 'coach' ? 'Coach' : 'Athlete';
  const planLabel = props.plan ? PLAN_BADGES[props.plan] ?? 'PRO' : 'BASIC';
  const needsPayment = Boolean(props.billingRequired);
  const isDisabled = props.status !== 'active' || needsPayment;

  const openTeam = () => {
    navigate(`/teams/${props.teamId}/dashboard`);
  };

  const openBilling = (event: React.MouseEvent) => {
    event.stopPropagation();
    navigate('/billing');
  };

  return (
    <Card
      onClick={openTeam}
      sx={{
        width: '100%',
        position: 'relative',
        cursor: 'pointer',
        borderLeft: `4px solid ${needsPayment ? '#D97706' : borderColor}`,
        borderRadius: '8px',
        boxShadow: '0px 1px 4px rgba(0,0,0,0.08)',
        '&:hover': {
          boxShadow: '0px 3px 10px rgba(0,0,0,0.12)',
        },
        backgroundColor: needsPayment ? '#FFFBEB' : '#FFFFFF',
        overflow: 'hidden',
        opacity: props.status !== 'active' ? 0.7 : 1,
      }}
    >
      {props.status !== 'active' && !needsPayment ? (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.25)',
            zIndex: 1,
            pointerEvents: 'none',
          }}
        />
      ) : null}

      <CardContent sx={{ p: 2, '&:last-child': { pb: 0 } }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '8px',
              backgroundColor: needsPayment ? '#FEF3C7' : '#EFF6FF',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {needsPayment ? (
              <LockOutlinedIcon sx={{ color: '#D97706', fontSize: 22 }} />
            ) : (
              <GroupsIcon sx={{ color: '#2563EB', fontSize: 22 }} />
            )}
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={600} noWrap sx={{ lineHeight: 1.3 }}>
              {props.name}
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap sx={{ fontSize: '0.75rem' }}>
              {needsPayment ? 'Payment required to keep using this team' : props.description}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: needsPayment ? '#D97706' : borderColor,
                  }}
                />
                <Typography variant="caption" fontWeight={500} color="text.secondary">
                  {roleLabel}
                </Typography>
              </Box>
              <Box
                sx={{
                  px: 0.75,
                  py: 0.15,
                  borderRadius: '4px',
                  border: needsPayment ? '1px solid #F59E0B' : '1px solid #D1D5DB',
                  backgroundColor: needsPayment ? '#FEF3C7' : 'transparent',
                  display: 'inline-flex',
                }}
              >
                <Typography
                  variant="caption"
                  fontWeight={600}
                  sx={{ fontSize: '0.65rem', color: needsPayment ? '#92400E' : '#6B7280' }}
                >
                  {needsPayment ? 'PAYMENT DUE' : planLabel}
                </Typography>
              </Box>
            </Box>
          </Box>

          <ChevronRightIcon sx={{ color: '#9CA3AF', mt: 0.5, flexShrink: 0 }} />
        </Box>

        {needsPayment ? (
          <Box sx={{ mt: 1.5, mb: 1.5 }}>
            <Typography variant="body2" sx={{ color: '#92400E', mb: 1, fontSize: '0.8rem' }}>
              {props.role === 'coach'
                ? 'Payment is due. Pay on Billing to unlock coaching actions.'
                : 'This team is locked until the coach completes payment.'}
            </Typography>
            {props.role === 'coach' ? (
              <Button
                size="small"
                variant="contained"
                onClick={openBilling}
                sx={{
                  textTransform: 'none',
                  borderRadius: '8px',
                  backgroundColor: '#D97706',
                  '&:hover': { backgroundColor: '#B45309' },
                }}
              >
                Go to Billing
              </Button>
            ) : null}
          </Box>
        ) : (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-around', pb: 1 }}>
              {[
                { value: props.athleteCount ?? 0, label: 'ATHLETES' },
                { value: props.coachCount ?? 0, label: 'COACHES' },
                { value: props.sessionCount ?? 0, label: 'SESSIONS' },
              ].map((stat) => (
                <Box key={stat.label} sx={{ textAlign: 'center' }}>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                    {stat.value}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: '0.6rem', letterSpacing: '0.04em' }}
                  >
                    {stat.label}
                  </Typography>
                </Box>
              ))}
            </Box>
          </>
        )}
      </CardContent>

      {isDisabled && needsPayment ? (
        <Box
          sx={{
            position: 'absolute',
            top: 10,
            right: 10,
            zIndex: 2,
            px: 1,
            py: 0.25,
            borderRadius: '4px',
            backgroundColor: '#F59E0B',
            color: '#fff',
            fontSize: '0.65rem',
            fontWeight: 700,
            letterSpacing: '0.04em',
          }}
        >
          LOCKED
        </Box>
      ) : null}
    </Card>
  );
};

export default COETeamCardComponent;
